import { AUTH_PATHS } from "@/constants/paths";
import { Pages } from "@/constants/enums";
import useFormFields from "@/hooks/useFormFields";
import useFormValidations from "@/hooks/useFormValidations";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/features/auth/store";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/shared/loader";
import FormFields from "@/components/shared/form-fields/form-fields";
import { authService } from "@/features/auth/services/authService";
import { forgotPasswordSchema } from "@/validations/password-reset";
import type { z } from "zod";
import { cn } from "@/lib/utils";
import { Form } from "@/components/ui/form";
import { getErrorMessage } from "@/lib/error-handler";
import { toArabicAuthError } from "@/features/auth/auth-messages";
import { AlertCircle, ArrowRight } from "lucide-react";

type ForgotPasswordSchema = z.infer<typeof forgotPasswordSchema>;

export default function ForgotPasswordForm() {
  const { getFormFields } = useFormFields({ slug: Pages.FORGOT_PASSWORD });
  const { getValidationSchema } = useFormValidations({
    slug: Pages.FORGOT_PASSWORD,
  });
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Recovery is an unauthenticated flow: drop any lingering session so the form
  // and its "back to sign in" link don't bounce a signed-in tester to /admin.
  const clearAuth = useAuthStore((s) => s.clearAuth);
  useEffect(() => {
    clearAuth();
  }, [clearAuth]);

  const formMethods = useForm<ForgotPasswordSchema>({
    resolver: zodResolver(getValidationSchema() as typeof forgotPasswordSchema),
    mode: "onChange",
    defaultValues: {
      email: "",
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = formMethods;

  const onSubmit = async (data: ForgotPasswordSchema) => {
    setErrorMessage(null);

    try {
      await authService.requestPasswordReset(data.email);

      const params = new URLSearchParams({ email: data.email });
      navigate(`${AUTH_PATHS.FORGOT_PASSWORD_SENT}?${params.toString()}`);
    } catch (error) {
      const message = getErrorMessage(error);
      setErrorMessage(toArabicAuthError(message, message));
    }
  };

  return (
    <Form {...formMethods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={cn(
          "flex flex-col bg-card p-4 rounded-lg shadow w-full max-w-md",
        )}
      >
        <div className="mb-4 space-y-1 text-center">
          <h2 className="text-lg font-semibold text-foreground">
            نسيت كلمة المرور؟
          </h2>
          <p className="text-sm text-muted-foreground">
            أدخل بريدك الإلكتروني وسنرسل لك رابط إعادة التعيين
          </p>
        </div>

        {errorMessage && (
          <div className="flex items-start gap-2 mb-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {getFormFields().map((field) => (
          <div key={field.name}>
            <FormFields {...field} control={control} errors={errors} />
          </div>
        ))}

        <Button
          type="submit"
          disabled={isSubmitting}
          className={cn("w-full h-10 mt-4 text-white font-medium")}
        >
          <LoadingButton
            loading={isSubmitting}
            loadingText="جار الإرسال..."
            loaderSize="sm"
          >
            إرسال رابط إعادة التعيين
          </LoadingButton>
        </Button>

        <Button asChild variant="ghost" className="mt-2">
          <Link to={AUTH_PATHS.LOGIN}>
            <ArrowRight className="ml-2 h-4 w-4" />
            العودة لتسجيل الدخول
          </Link>
        </Button>
      </form>
    </Form>
  );
}
